import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Play, Pause, Download, Clock, Headphones, Video, Music2, 
  ChevronRight, Filter, Search, Lock, ExternalLink, SkipBack, SkipForward, Volume2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';
import { InlinePodcastField } from '@/components/edit/InlinePodcastField';
import { InlinePodcastImage } from '@/components/edit/InlinePodcastImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PodcastsInner() {
  const [podcasts, setPodcasts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMemberPrompt, setShowMemberPrompt] = useState(false);
  const audioRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getValue, enabled } = usePageEditMode();

  const handlePodcastSaved = (updated) => {
    setPodcasts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    if (currentPodcast?.id === updated.id) {
      setCurrentPodcast((cp) => ({ ...cp, ...updated }));
    }
  };

  useEffect(() => {
    fetchPodcasts();
    fetchCategories();
  }, []);

  const fetchPodcasts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/podcasts`);
      const data = await response.json();
      setPodcasts(data.podcasts || []);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/podcasts/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const playPodcast = async (podcast) => {
    if (currentPodcast?.id === podcast.id && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      setCurrentPodcast(podcast);
      setIsPlaying(true);
      // Record play
      try {
        await fetch(`${API_URL}/api/podcasts/${podcast.id}/play`, { method: 'POST' });
      } catch (e) {}
    }
  };

  const handleDownload = async (podcast) => {
    if (podcast.members_only_download && !user) {
      setShowMemberPrompt(true);
      return;
    }
    
    // Record download
    try {
      await fetch(`${API_URL}/api/podcasts/${podcast.id}/download`, { method: 'POST' });
    } catch (e) {}
    
    // Trigger download
    const link = document.createElement('a');
    link.href = podcast.audio_url || podcast.video_url;
    link.download = `${podcast.title}.${podcast.is_video ? 'mp4' : 'mp3'}`;
    link.click();
  };

  useEffect(() => {
    if (audioRef.current && currentPodcast) {
      audioRef.current.src = currentPodcast.audio_url;
      if (isPlaying) {
        audioRef.current.play();
      }
    }
  }, [currentPodcast]);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  const filteredPodcasts = podcasts.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black overflow-x-hidden">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10" />
        <div className="max-w-6xl mx-auto px-6 py-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Headphones className="w-8 h-8 text-purple-400" />
              <span className="text-purple-400 font-medium">
                <PageEditable path="hero.kicker" as="span" fallback="CASA SAMBA PODCASTS" />
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <PageEditable path="hero.titlePre" as="span" fallback="Listen &" /> <span className="text-purple-400"><PageEditable path="hero.titleAccent" as="span" fallback="Learn" /></span>
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl">
              <PageEditable
                path="hero.subtitle"
                as="span"
                multiline
                fallback="Dive deep into Afro-Brasilian culture, drumming techniques, and stories from Mestre Curtis Pierre."
              />
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-purple-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <PageEditable path="filters.allLabel" fallback="All Episodes" />
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  selectedCategory === cat
                    ? 'bg-purple-500 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder={getValue('filters.searchPlaceholder') || 'Search episodes...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-full text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 w-64"
            />
            {enabled && (
              <div className="mt-2 text-xs text-zinc-500">
                Placeholder: <PageEditable path="filters.searchPlaceholder" fallback="Search episodes..." />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Podcast Grid */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-zinc-500 mt-4">
              <PageEditable path="states.loading" fallback="Loading podcasts..." />
            </p>
          </div>
        ) : filteredPodcasts.length === 0 ? (
          <div className="text-center py-12">
            <Headphones className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">
              <PageEditable path="states.emptyTitle" fallback="No Episodes Yet" />
            </h3>
            <p className="text-zinc-500">
              <PageEditable path="states.emptyBody" fallback="Check back soon for new content!" />
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredPodcasts.map((podcast, idx) => (
              <motion.div
                key={podcast.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`bg-zinc-900/50 border rounded-2xl p-6 ${
                  currentPodcast?.id === podcast.id 
                    ? 'border-purple-500' 
                    : 'border-zinc-800 hover:border-zinc-700'
                } transition-colors`}
              >
                <div className="flex gap-6">
                  {/* Cover Image */}
                  <div className="flex-shrink-0">
                    {podcast.cover_url ? (
                      <InlinePodcastImage
                        podcast={podcast}
                        className="w-32 h-32 rounded-xl overflow-hidden"
                        onSaved={handlePodcastSaved}
                      >
                        <img loading="lazy" decoding="async" 
                          src={podcast.cover_thumb_url || podcast.cover_display_url || podcast.cover_url} 
                          alt={podcast.title}
                          className="w-32 h-32 rounded-xl object-cover"
                        />
                      </InlinePodcastImage>
                    ) : (
                      <InlinePodcastImage
                        podcast={podcast}
                        className="w-32 h-32 rounded-xl overflow-hidden"
                        onSaved={handlePodcastSaved}
                      >
                        <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center">
                          {podcast.is_video ? (
                            <Video className="w-12 h-12 text-purple-400" />
                          ) : (
                            <Music2 className="w-12 h-12 text-purple-400" />
                          )}
                        </div>
                      </InlinePodcastImage>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <InlinePodcastField
                          podcast={podcast}
                          field="category"
                          as="span"
                          className="text-xs text-purple-400 font-medium uppercase tracking-wider"
                          onSaved={handlePodcastSaved}
                        />
                        {(podcast.series || enabled) && (
                          <>
                            <span className="text-xs text-zinc-500 ml-2">• </span>
                            <InlinePodcastField
                              podcast={podcast}
                              field="series"
                              as="span"
                              className="text-xs text-zinc-500"
                              onSaved={handlePodcastSaved}
                            />
                          </>
                        )}
                        <InlinePodcastField
                          podcast={podcast}
                          field="title"
                          as="h3"
                          className="text-xl font-bold text-white mt-1"
                          onSaved={handlePodcastSaved}
                        />
                        <InlinePodcastField
                          podcast={podcast}
                          field="description"
                          as="p"
                          className="text-zinc-400 mt-2 line-clamp-2"
                          onSaved={handlePodcastSaved}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-4">
                      {/* Play Button */}
                      {(podcast.audio_url || podcast.video_url) && (
                        <button
                          onClick={() => playPodcast(podcast)}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-400 text-white font-medium rounded-full transition-colors"
                        >
                          {currentPodcast?.id === podcast.id && isPlaying ? (
                            <>
                              <Pause className="w-4 h-4" />
                              <PageEditable path="buttons.pause" fallback="Pause" />
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4" />
                              <PageEditable path="buttons.play" fallback="Play" />
                            </>
                          )}
                        </button>
                      )}

                      {/* Embed Link */}
                      {podcast.embed_url && (
                        <a
                          href={podcast.embed_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded-full transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <PageEditable path="buttons.listenOnPlatform" fallback="Listen on Platform" />
                        </a>
                      )}

                      {/* Download Button */}
                      {(podcast.audio_url || podcast.video_url) && (
                        <button
                          onClick={() => handleDownload(podcast)}
                          className="flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded-full transition-colors"
                          title={podcast.members_only_download && !user ? "Members only" : "Download"}
                        >
                          {podcast.members_only_download && !user ? (
                            <Lock className="w-4 h-4" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          <PageEditable path="buttons.download" fallback="Download" />
                        </button>
                      )}

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-sm text-zinc-500 ml-auto">
                        <span className="flex items-center gap-1">
                          <Play className="w-3 h-3" />
                          {podcast.play_count || 0} <PageEditable path="stats.plays" fallback="plays" />
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(podcast.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Audio Player (Fixed at bottom) */}
      {currentPodcast && (
        <div className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-zinc-800 p-4 z-50">
          <div className="max-w-6xl mx-auto flex items-center gap-4">
            {currentPodcast.cover_url ? (
              <img loading="lazy" decoding="async" src={currentPodcast.cover_thumb_url || currentPodcast.cover_display_url || currentPodcast.cover_url} alt="" className="w-12 h-12 rounded-lg" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Music2 className="w-6 h-6 text-purple-400" />
              </div>
            )}
            <div className="flex-1">
              <h4 className="font-medium text-white">{currentPodcast.title}</h4>
              <p className="text-sm text-zinc-500">{currentPodcast.category}</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-12 h-12 rounded-full bg-purple-500 hover:bg-purple-400 flex items-center justify-center transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-white" />
                ) : (
                  <Play className="w-5 h-5 text-white ml-1" />
                )}
              </button>
            </div>
          </div>
          <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
        </div>
      )}

      {/* Member Prompt Modal */}
      {showMemberPrompt && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowMemberPrompt(false)}
        >
          <div 
            className="bg-zinc-900 border border-purple-500/30 rounded-2xl p-8 max-w-md w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">
              <PageEditable path="memberPrompt.title" as="span" fallback="Members Only Download" />
            </h3>
            <p className="text-zinc-400 mb-6">
              <PageEditable
                path="memberPrompt.body"
                as="span"
                multiline
                fallback="Podcast downloads are exclusive to Escola de Samba Casa Samba members. Sign in to download this episode."
              />
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowMemberPrompt(false);
                  navigate('/admin');
                }}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl"
              >
                <PageEditable path="memberPrompt.cta" as="span" fallback="Sign In" />
              </button>
              <button
                onClick={() => setShowMemberPrompt(false)}
                className="text-zinc-500 hover:text-zinc-300 text-sm"
              >
                <PageEditable path="memberPrompt.dismiss" as="span" fallback="Maybe Later" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Podcasts() {
  return (
    <PageEditProvider slug="podcasts">
      <PodcastsInner />
      <PageEditToolbar />
      <AdminEditorLink to="/admin/podcasts" label="Edit Podcasts (Dashboard)" />
    </PageEditProvider>
  );
}
